/**
 * @file
 * this file contains all grid options that are used in both propose_team_member_overview_table and propose_vacancy_table
 */

import React from "react";

import { TeamLeadSolutionType } from "@src/constants";
import { TextSubtextRenderer } from "@src/custom_renderer";
import { RadioButtonRenderer } from "@src/custom_renderer/radio_button_renderer";
import { ProposeAllocationRenderer } from "@src/features/staffing_request_details/custom_renderer/propose_allocation_renderer";
import { SearchInput } from "@src/features/table_filtering/components/search_input";
import { TeamMemberCapacityV2 } from "@src/types";
import { FrameworkComponents } from "@src/types/aggrid_types";
import { TextSubtextRendererValue } from "@src/types/custom_renderer_types";
import {
  AllocationRowData,
  ProposeAllocationRowData,
} from "@src/types/role_request_types";
import { columnTypes, groupHeaderStyleOptions } from "@src/utils/aggrid_utils";
import { getAllocationColumnDefs } from "@src/utils/allocation_column_def_utils";
import {
  CellClassParams,
  ColDef,
  FilterModel,
  GetRowIdParams,
  GridOptions,
  ValueGetterParams,
} from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import dayjs from "dayjs";
import _ from "lodash";

import { valueFormatter } from "./propose_allocations_utils";

/**
 * Get the grid options
 *
 * @returns {GridOptions} The grid options
 */
export function getProposeTableGridOptions(): GridOptions {
  return {
    ...groupHeaderStyleOptions,
    components: proposeTeamMemberComponents(),
    suppressContextMenu: true,
    masterDetail: true,
    embedFullWidthRows: true,
    detailRowHeight: 40,
    animateRows: false,
    defaultColDef: {
      ...columnTypes.default,
      resizable: false,
      sortable: false,
    },
    rowSelection: {
      mode: "singleRow",
      checkboxes: false,
    },
    getRowId: (params: GetRowIdParams<ProposeAllocationRowData>) => {
      return params.data.employee?.employeeId?.toString() ?? params.data.label;
    },
    detailCellRenderer: (props: CustomCellRendererProps) => (
      <ProposeAllocationRenderer props={props} />
    ),
  };
}

/**
 * Propose team member components
 * @returns {FrameworkComponents} The framework components
 */
function proposeTeamMemberComponents(): FrameworkComponents {
  return {
    renderTextSubtext: (props: CustomCellRendererProps) => {
      return <TextSubtextRenderer props={props} variant={"small-blue"} />;
    },
    radioButtonRenderer: (
      props: CustomCellRendererProps<TeamMemberCapacityV2>
    ) => {
      return <RadioButtonRenderer params={props} />;
    },
  };
}

/**
 * Get the column definitions for the grid
 *
 * @param requestEndDate    The request end date
 * @returns {ColDef[]} The column definitions
 */
export function getColumnDefs(
  solutionType:
    | TeamLeadSolutionType.PROPOSE_TEAM_MEMBER
    | TeamLeadSolutionType.VACANCY,
  workloadEndDate: Date
): ColDef[] {
  const workloadStartDate = new Date();

  const employeeColumn: ColDef =
    solutionType === TeamLeadSolutionType.VACANCY
      ? getVacancyColumnDefs()
      : getTeamMemberColumnDefs();

  const allocationColumns: ColDef[] = getAllocationColumnDefs(
    workloadStartDate,
    workloadEndDate,
    getColDefMonth
  );

  return [
    {
      width: 10,
      pinned: "left",
      field: "label",
      cellRendererSelector: (props: CustomCellRendererProps) =>
        !props.node.rowPinned && { component: "radioButtonRenderer" },
      colSpan: (params: CellClassParams) => {
        return params.node.rowPinned && 2;
      },
    },
    {
      field: "employee",
      headerName: "",
      pinned: "left",
      width: 250,
      filter: "agTextColumnFilter",
      cellRenderer: "renderTextSubtext",
      resizable: true,
      headerComponent: (
        props: CustomCellRendererProps<TeamMemberCapacityV2>
      ) => (
        <SearchInput
          onChange={(searchValue: string) => onSearch(props, searchValue)}
        />
      ),
      comparator: (
        valueA: TextSubtextRendererValue,
        valueB: TextSubtextRendererValue
      ) => {
        return valueA.text > valueB.text ? 1 : -1;
      },
      ...employeeColumn,
    },
    ...allocationColumns,
  ];
}

/**
 * Gets the column defs for team members
 *
 * @returns column defs for team members
 */
function getTeamMemberColumnDefs(): ColDef {
  return {
    minWidth: 130,
    filterValueGetter: (params: ValueGetterParams) => {
      return `${params.data.employee?.fullName} ${params.data.employee?.genericRoleName}`;
    },
    valueGetter: (params: ValueGetterParams): TextSubtextRendererValue => {
      return {
        text: params.data.employee?.fullName,
        subtext: params.data.employee?.genericRoleName,
      };
    },
  };
}

/**
 * Gets the column defs for vacancies
 *
 * @returns column defs for vacancies
 */
function getVacancyColumnDefs(): ColDef {
  return {
    minWidth: 180,
    filterValueGetter: (params: ValueGetterParams) => {
      return `${params.data.employee?.deskId} ${params.data.employee?.positionText}`;
    },
    valueGetter: (params: ValueGetterParams): TextSubtextRendererValue => {
      const startDate: Date = params.data.employee?.positionStartDate;
      const endDate: Date = params.data.employee?.positionEndDate;

      const dateRange: string =
        startDate && endDate
          ? `${dayjs(startDate).format("MM/YYYY")} - ${dayjs(endDate).format(
              "MM/YYYY"
            )} · `
          : "";

      const contractType: string =
        _.capitalize(params.data.employee?.contractType) || "No Contract Type";

      return {
        text: `${params.data.employee?.deskId} - ${params.data.employee?.positionText}`,
        subtext: `${dateRange}${contractType}`,
      };
    },
  };
}

/**
 * Get the column definition for the month
 *
 * @param year     The year
 * @param month    The month
 */
function getColDefMonth(year: number, month: number): ColDef {
  return {
    cellClass: "allocation-cell no-focus-border",
    valueGetter: (params: ValueGetterParams) =>
      customValueGetter(params, year, month),
    valueFormatter: valueFormatter,
  };
}

/**
 * Get the custom value getter
 *
 * @param params The value getter parameters
 * @param year The year
 * @param month The month
 *
 * @returns The custom value
 */
function customValueGetter(
  params: ValueGetterParams,
  year: number,
  month: number
): number {
  const foundAllocation: AllocationRowData = params.data.allocations.find(
    (allocation: AllocationRowData) =>
      dayjs(allocation.date).month() + 1 === month &&
      dayjs(allocation.date).year() === year
  );
  return foundAllocation?.percentage || 0;
}

/**
 * Handle the search event
 *
 * @param props The search component properties
 * @param searchValue The search value
 */
function onSearch(
  props: CustomCellRendererProps<TeamMemberCapacityV2>,
  searchValue: string
): void {
  const filter: FilterModel = {
    [props.column.getColId()]: {
      filterType: "text",
      type: "contains",
      filter: searchValue,
    },
  };

  props.api.setFilterModel(filter);
}
